import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ServicesListTable from "./ServicesListTable";
import ServicesFilter from "./ServicesFilter";
import AppPagination from "../../../components/AppPagination";
import ConfirmationDialog from "../../../components/ConfirmationDialog";

import { getProjectsList } from "../../../actions/projectsActions";
import { resetProjectsState } from "../../../slices/projectsSlice";
import { seedDefaultServices } from "../../../actions/servicesActions";

const defaultFilter = {
  page: 1,
  name: "",
};

const ServicesHome = () => {
  const dispatch = useDispatch();
  const projects = useSelector((state) => state.projects.list);

  const [filter, setFilter] = useState({ ...defaultFilter });
  const [pagination, setPagination] = useState({ page: 1 });
  const [showSeedConfirm, setShowSeedConfirm] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    setPagination({ page: 1 });
    return () => dispatch(resetProjectsState());
  }, []);

  useEffect(() => {
    const f = { page: pagination.page };
    filter.name.length > 0 ? (f.name = filter.name) : null;
    dispatch(getProjectsList({ query: f }));
  }, [pagination]);

  const handleChange = (e) =>
    setFilter({ ...filter, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setPagination({ page: 1 });
  };

  const onClear = () => {
    setFilter({ ...defaultFilter });
    setPagination({ page: 1 });
  };

  const nextPage = () => setPagination({ page: pagination.page + 1 });
  const prevPage = () =>
    setPagination({ page: Math.max(pagination.page - 1, 1) });

  const onRefresh = () => {
    dispatch(getProjectsList({ query: { page: pagination.page } }));
  };

  const openSeedConfirm = () => setShowSeedConfirm(true);
  const closeSeedConfirm = () => setShowSeedConfirm(false);

  const handleSeedServices = () => {
    dispatch(
      seedDefaultServices({
        defaultConfig: {},
        callback: (response) => {
          closeSeedConfirm();
          setSeedResult(response);
          setShowFeedback(true);
          onRefresh();
        },
      })
    );
  };

  const closeFeedback = () => {
    setShowFeedback(false);
    setSeedResult(null);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ marginBottom: "1rem" }}
        >
          Servicios por Proyecto
        </Typography>
        <Button
          color="primary"
          startIcon={<AddIcon />}
          onClick={openSeedConfirm}
        >
          Generar Servicios
        </Button>
      </Box>
      <ServicesFilter
        {...filter}
        onChange={handleChange}
        onSubmit={onSubmit}
        onClear={onClear}
      />
      <ServicesListTable projects={projects} onRefresh={onRefresh} />
      <AppPagination page={pagination.page} prev={prevPage} next={nextPage} />
      <ConfirmationDialog
        open={showSeedConfirm}
        onClose={closeSeedConfirm}
        onConfirm={handleSeedServices}
        title="Generar Servicios"
        message="Se generarán servicios automáticos para todos los proyectos que no tengan uno configurado. ¿Deseas continuar?"
      />
      <Dialog open={showFeedback} onClose={closeFeedback}>
        <DialogTitle>Resultado de la operación</DialogTitle>
        <DialogContent>
          {seedResult && (
            <Box sx={{ minWidth: 300 }}>
              <Typography sx={{ mb: 1 }}>{seedResult.message}</Typography>
              <Typography variant="body2">
                Proyectos procesados: <strong>{seedResult.projectsProcessed}</strong>
              </Typography>
              <Typography variant="body2">
                Servicios creados: <strong>{seedResult.servicesCreated}</strong>
              </Typography>
              <Typography variant="body2">
                Servicios omitidos: <strong>{seedResult.servicesSkipped}</strong>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFeedback}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServicesHome;
